import { ThemeProvider } from '@mui/material';
import { FunctionComponent, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { PhaseTransition } from '../../components/animation/PhaseTransition';
import { RoundTransition } from '../../components/animation/RoundTransition';
import { WelcomeSequence } from '../../components/animation/WelcomeSequence';
import { BackgroundDim } from '../../components/game/BackgroundDim';
import { ControlPanel } from '../../components/game/ControlPanel';
import { FaceOffOverlay } from '../../components/game/FaceOffOverlay';
import { FaceOffPortalProvider } from '../../components/game/FaceOffPortal';
import { GameBoardLayout } from '../../components/game/GameBoardLayout';
import { GameplayErrorMessage } from '../../components/game/GameplayErrorMessage';
import { QuestionWithAnswers } from '../../components/game/Question';
import { Scores } from '../../components/game/Scores';
import { StrikeIndicator } from '../../components/game/StrikeIndicator';
import { ScreenTitle } from '../../components/ScreenTitle';
import { GameTheme } from '../../config/GameTheme';
import '../../GameplaySounds';
import { gamePlayStore, useGameplaySelector } from '../../store/GameplayStore';
import { GamePhase } from '../../types/GameplayTypes';
import { useAsyncAnimation } from '../../util/Animation';

export const GameplayScreen: FunctionComponent = () => {
	const round = useGameplaySelector(gamePlayStore.selectors.round);
	const phase = useGameplaySelector(gamePlayStore.selectors.phase);
	const previousPhase = useRef<GamePhase>(phase);
	const navigate = useNavigate();

	const welcomeAnim = useAsyncAnimation('Welcome', { initialShow: phase === 'faceoff' });
	const roundAnim = useAsyncAnimation('Next Round');
	const phaseAnim = useAsyncAnimation('Phase');
	const dimAnim = useAsyncAnimation('Background Dim', { hideOnFinish: false });
	const faceOffAnim = useAsyncAnimation('Face Off', { hideOnFinish: false });

	const headToHead = (phase === 'faceoff') || (phase === 'playOrPass');

	useEffect(() => {
		async function welcomeSequence() {
			if ((round === 0) && (phase === 'faceoff')) {
				await Promise.all([
					welcomeAnim.play(),
					roundAnim.play(),
				]);
				await Promise.all([
					dimAnim.play(),
					faceOffAnim.play(),
				]);
			}
		}
		void welcomeSequence();
	}, [phase]);

	useEffect(() => {
		async function showNewRound() {
			// First round is handled in welcome sequence
			if (round > 0) {
				await roundAnim.play();
				await Promise.all([
					dimAnim.play({ direction: headToHead ? 'forward' : 'reverse' }),
					faceOffAnim.play({ direction: headToHead ? 'forward' : 'reverse' }),
				]);
			}
		}
		void showNewRound();
	}, [round]);

	useEffect(function handlePhaseChange() {
		async function showPhaseChange() {
			const isPhaseChange = phase !== previousPhase.current;

			if (isPhaseChange) {
				switch (phase) {
				case 'setup':
				case 'faceoff':
				case 'playOrPass':
					break;

				default:
					await phaseAnim.play();
					// Deactivate head-to-head
					await Promise.all([
						dimAnim.play({ direction: 'reverse' }),
						faceOffAnim.play({ direction: 'reverse' }),
					]);
				}
			}

			previousPhase.current = phase;
		}

		void showPhaseChange();
	}, [phase]);

	useEffect(function goToGameFinishedScreen() {
		if (phase === 'gameEnd') {
			void navigate('/game-finished');
		}
	}, [phase]);

	return <ThemeProvider theme={GameTheme}>
		<ScreenTitle title='Play' />
		<FaceOffPortalProvider>
			<RoundTransition roundNumber={round} {...roundAnim.props} />
			<WelcomeSequence {...welcomeAnim.props} />
			<PhaseTransition phase={phase} {...phaseAnim.props} />
			<GameBoardLayout
				scores={<Scores />}
				question={<QuestionWithAnswers />}
				strikes={<StrikeIndicator />}
			/>
			<BackgroundDim {...dimAnim.props} />
			<FaceOffOverlay {...faceOffAnim.props} />
			<GameplayErrorMessage />
			<ControlPanel />
		</FaceOffPortalProvider>
	</ThemeProvider>;
};
